import type {ReactNode} from 'react';

import {
  BarcodeOutlined,
  FundFilled,
  FundOutlined,
  HomeFilled,
  HomeOutlined,
  RiseOutlined,
  ShoppingFilled,
  ShoppingOutlined,
  UserOutlined
} from '@ant-design/icons';
import {BottomTabBar, BottomTabBarItem} from '@ds/layout';
import {useFlag} from '@unleash/proxy-client-react';
import {memo, useMemo} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useAppSelector} from 'store';

type NavBadge = {
  id: number;
  text: string;
};

type NavIcon = 'home' | 'up' | 'order' | 'analytic' | 'products' | 'user';

type NavItem = {
  id: number;
  title: string;
  icon: NavIcon;
  url: string;
  badge?: string;
};

type NavIconSet = {
  solid: ReactNode;
  regular: ReactNode;
};

const navIcons: Record<NavIcon, NavIconSet> = {
  home: {
    solid: <HomeFilled aria-hidden className="text-xl" />,
    regular: <HomeOutlined aria-hidden className="text-xl" />
  },
  up: {
    solid: <RiseOutlined aria-hidden className="text-xl" />,
    regular: <RiseOutlined aria-hidden className="text-xl" />
  },
  order: {
    solid: <ShoppingFilled aria-hidden className="text-xl" />,
    regular: <ShoppingOutlined aria-hidden className="text-xl" />
  },
  analytic: {
    solid: <FundFilled aria-hidden className="text-xl" />,
    regular: <FundOutlined aria-hidden className="text-xl" />
  },
  products: {
    solid: <BarcodeOutlined aria-hidden className="text-xl" />,
    regular: <BarcodeOutlined aria-hidden className="text-xl" />
  },
  user: {
    solid: <UserOutlined aria-hidden className="text-xl" />,
    regular: <UserOutlined aria-hidden className="text-xl" />
  }
};

function getActiveNavId(pathname: string): number {
  if (pathname === '/home' || pathname.startsWith('/home/')) {
    return 1;
  }
  if (pathname.includes('/services/promotion-center')) {
    return 2;
  }
  if (pathname.includes('/orders')) {
    return 3;
  }
  if (pathname.includes('/products')) {
    return 4;
  }
  if (pathname.includes('/chortke')) {
    return 5;
  }
  if (pathname.includes('/profile')) {
    return 6;
  }
  return 0;
}

type BottomNavigationProps = {
  badges: NavBadge[];
};

const BottomNavigation = ({badges}: BottomNavigationProps) => {
  const isCustomDealEnabled = useFlag('discountCustomDeal');
  const navigate = useNavigate();
  const location = useLocation();
  const {selectedVendor} = useAppSelector((state) => state.dashboard);

  const badgeById = useMemo(
    () => Object.fromEntries(badges.map((badge) => [badge.id, badge.text])),
    [badges]
  );

  const items = useMemo<NavItem[]>(() => {
    const fourthTab: NavItem = isCustomDealEnabled
      ? {
          id: 4,
          title: 'محصولات',
          icon: 'products',
          url: '/products/list',
          badge: badgeById[6]
        }
      : {
          id: 5,
          title: 'چرتکه',
          icon: 'analytic',
          url: '/chortke',
          badge: badgeById[4]
        };

    return [
      {id: 1, title: 'نما', icon: 'home', url: '/home', badge: badgeById[1]},
      {
        id: 2,
        title: 'ترقی',
        icon: 'up',
        url: '/services/promotion-center',
        badge: badgeById[2]
      },
      {id: 3, title: 'سفارش‌ها', icon: 'order', url: '/orders', badge: badgeById[3]},
      fourthTab,
      {
        id: 6,
        title: 'حساب شما',
        icon: 'user',
        url: `/profile/${selectedVendor?.id}`,
        badge: badgeById[5]
      }
    ];
  }, [badgeById, isCustomDealEnabled, selectedVendor?.id]);

  const activeId = getActiveNavId(location.pathname);

  const handleClickItem = (item: NavItem) => {
    if (!item.url || location.pathname === item.url) {
      return;
    }

    navigate({pathname: item.url});
  };

  return (
    <BottomTabBar>
      {items.map((item) => {
        const selected = item.id === activeId;
        const iconSet = navIcons[item.icon];

        return (
          <BottomTabBarItem
            key={item.id}
            label={item.title}
            selected={selected}
            badge={item.badge}
            icon={selected ? iconSet.solid : iconSet.regular}
            onClick={() => handleClickItem(item)}
          />
        );
      })}
    </BottomTabBar>
  );
};

export default memo(BottomNavigation);
